import React, {useCallback, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import {
    Box,
    Grid,
    Text,
    AspectRatio,
    Skeleton,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'
import Breadcrumb from '../../../components/breadcrumb'
import Badges from '../../../components/product-badges'
import ProductSetGrid from './product-set-grid'

// Lazy load ProductZoomModal - only loaded when the user clicks to zoom
const ProductZoomModal = loadable(() => import('../../../components/product-zoom-modal'), {
    ssr: false
})

const VIEW_TYPE_LARGE = 'large'
const VIEW_TYPE_ZOOM = 'zoom'

// Custom magnifying glass cursor (matches image-gallery)
const MAGNIFYING_GLASS_CURSOR = `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='rgba(255,255,255,0.85)' d='M0 0h28v28H0z'/%3E%3Cg clip-path='url(%23a)'%3E%3Cpath d='M12.33 9.833v5m-2.5-2.5h5m7.09 9.59-4.59-4.59m-5 1.667a6.667 6.667 0 1 0 0-13.334 6.667 6.667 0 0 0 0 13.334Z' stroke='%2325252B' stroke-width='1.1' stroke-linecap='square'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M4 4h20v20H4z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E") 12 12, zoom-in`

const ProductSetView = ({product, category, isLoading}) => {
    const productName = product?.name || product?.productName
    const childProducts = product?.setProducts || []
    const isDesktop = useBreakpointValue({base: false, lg: true})

    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const resizeEnabled =
        getSiteEnvVarValue(appConfig, 'PRODUCT_IMGS_RESIZE_PARAMS_ENABLED', site?.id) === 'true'

    const heroImageGroup = useMemo(
        () =>
            findImageGroupBy(product?.imageGroups, {
                viewType: VIEW_TYPE_LARGE,
                selectedVariationAttributes: {}
            }),
        [product?.imageGroups]
    )
    const zoomImageGroup = useMemo(
        () =>
            findImageGroupBy(product?.imageGroups, {
                viewType: VIEW_TYPE_ZOOM,
                selectedVariationAttributes: {}
            }),
        [product?.imageGroups]
    )

    const heroImages = useMemo(() => heroImageGroup?.images || [], [heroImageGroup])
    const zoomImages = useMemo(
        () => zoomImageGroup?.images || heroImages,
        [zoomImageGroup, heroImages]
    )

    const mainImage = heroImages[0]
    const mainImageSrc = mainImage?.disBaseLink || mainImage?.link

    const [isZoomOpen, setIsZoomOpen] = useState(false)

    const getImageUrl = useCallback(
        (image) => {
            const baseUrl = image?.disBaseLink || image?.link
            return resizeEnabled ? `${baseUrl}[?sw={width}&q=75&fmt=webp]` : baseUrl
        },
        [resizeEnabled]
    )

    const handleOpenZoom = useCallback(() => {
        if (!zoomImages.length) return
        setIsZoomOpen(true)
    }, [zoomImages.length])

    const handleCloseZoom = useCallback(() => setIsZoomOpen(false), [])

    return (
        <Box data-testid="product-set-view">
            {isDesktop && (
                <Skeleton isLoaded={category} minWidth={64}>
                    {/* Breadcrumb */}
                    <Breadcrumb categories={category} variant="pdp" />
                </Skeleton>
            )}

            <Grid
                display="grid"
                templateColumns={{
                    base: '1fr',
                    md: '6fr 5fr',
                    xl: '4fr 4fr 1fr'
                }}
                gap={{base: 2, xl: 'min(105px, 9%)'}}
                className="wrapper-grid"
            >
                <Box
                    position="relative"
                    w={{base: 'auto', md: '100%'}}
                    maxW={{md: '560px', xl: '631px'}}
                    marginInline={{base: -2, md: 0}}
                    mb={{base: 4, lg: 0}}
                    className="wrapper-before-grid"
                >
                    <Box position="sticky" top={0}>
                        <Box position="relative">
                            <Box
                                position={'absolute'}
                                top={{lg: 1}}
                                bottom={{base: 1, lg: 'auto'}}
                                left={1}
                                zIndex={3}>
                                <Badges badges={product?.c_badges} />
                            </Box>
                            <AspectRatio ratio={2 / 3}>
                                {isLoading ? (
                                    <Skeleton isLoaded={!isLoading} />
                                ) : (
                                    <Box
                                        as="button"
                                        type="button"
                                        onClick={handleOpenZoom}
                                        aria-label={mainImage?.alt || productName}
                                        cursor={MAGNIFYING_GLASS_CURSOR}
                                        background="transparent"
                                        border="none"
                                        p={0}
                                        w="100%"
                                        h="100%"
                                        display="block"
                                    >
                                        <Box w="100%" h="100%">
                                            <DynamicImage
                                                data-testid="product-set-main-image"
                                                src={mainImageSrc}
                                                widths={['100vw', '50vw', '40vw', '40vw']}
                                                imageProps={{
                                                    alt: mainImage?.alt || productName,
                                                    loading: 'eager',
                                                    fetchpriority: 'high',
                                                    width: '100%'
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </AspectRatio>
                        </Box>
                    </Box>
                </Box>

                <Box 
                    w={{base: 'auto', md: '100%'}}
                    marginInline={{base: -2, md: 0}}
                    pl={{base: 0, md: 4, xl: 0}}
                    pr={{base: 0, lg: 'auto'}}
                    className="wrapper-set-products"
                >
                    <Skeleton isLoaded={!isLoading}>
                        <Text
                            as="h1"
                            textStyle="webH3"
                            mb={{base: 3, md: 4}}
                            px={{base: 2, lg: 0}}
                            data-testid="product-set-title"
                        >
                            {productName}
                        </Text>
                    </Skeleton>
                    <ProductSetGrid products={childProducts} isLoading={isLoading} showQV showSwatches />
                </Box>
            </Grid>

            <ProductZoomModal
                isOpen={isZoomOpen}
                onClose={handleCloseZoom}
                images={zoomImages}
                thumbImages={heroImages}
                initialImageIndex={0}
                getImageUrl={getImageUrl}
            />
        </Box>
    )
}

ProductSetView.propTypes = {
    product: PropTypes.object,
    category: PropTypes.array,
    isLoading: PropTypes.bool
}

export default ProductSetView
